import { debounce } from 'lodash'

import { MapLocationProps } from './MapTypes'

export const openGoogleDirectionMap = (destination: string) => {
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`
}

export const ZOOM = {
  default: 4,
  selected: 8,
}

// Checks if a new position is outside a bounding box around the last position
export const isOutsideBoundingBox = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  thresholdDegrees = 0.3,
): boolean => {
  return (
    Math.abs(lat2 - lat1) > thresholdDegrees ||
    Math.abs(lng2 - lng1) > thresholdDegrees
  )
}

const debouncedFetchStores = debounce(
  (
    center: MapLocationProps,
    lastSearchLocation: MapLocationProps | null,
    onFetchStores: (lat: number, lng: number) => void,
    onSetResultsFor: (location: string) => void,
    onUpdateLastSearchLocation: (location: MapLocationProps) => void,
  ) => {
    let shouldFetch = false

    if (!lastSearchLocation) {
      shouldFetch = true
    } else {
      // Check if outside bounding box
      shouldFetch = isOutsideBoundingBox(
        lastSearchLocation.lat,
        lastSearchLocation.lng,
        center.lat,
        center.lng,
      )
    }

    if (!shouldFetch) {
      return
    }

    try {
      onFetchStores(center.lat, center.lng)

      if (
        typeof window !== 'undefined' &&
        window.google &&
        window.google.maps
      ) {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode(
          { location: { lat: center.lat, lng: center.lng } },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              onSetResultsFor(results[0].formatted_address)
            }
          },
        )
      }
      onUpdateLastSearchLocation(center)
    } catch (error) {
      console.error('Error fetching stores:', error)
    }
  },
  1000,
  { leading: false, trailing: true },
)

export const cancelDebouncedFetch = () => {
  debouncedFetchStores.cancel()
}

export const handleMapChange = (
  center: MapLocationProps,
  zoom: number,
  options: {
    enableSearchOnMapLocationChange: boolean
    mapChangeCount: number
    lastSearchLocation: MapLocationProps | null
    onFetchStores: (lat: number, lng: number) => void
    onSetResultsFor: (location: string) => void
    onUpdateLastSearchLocation: (location: MapLocationProps) => void
  },
): void => {
  const {
    enableSearchOnMapLocationChange,
    mapChangeCount,
    lastSearchLocation,
    onFetchStores,
    onSetResultsFor,
    onUpdateLastSearchLocation,
  } = options

  if (!enableSearchOnMapLocationChange) {
    return
  }

  // Only fetch new stores when zoomed in enough to see details (zoom level 6 or higher)
  if (mapChangeCount > 1 && zoom >= 6) {
    debouncedFetchStores(
      center,
      lastSearchLocation,
      onFetchStores,
      onSetResultsFor,
      onUpdateLastSearchLocation,
    )
  }
}

// Creates a map load handler function for Google Maps
export const createMapLoadHandler = (options: {
  defaultZoom: number | undefined
  mapReadyCallback?: (ready: boolean) => void
  onMapChange?: (
    center: MapLocationProps,
    zoom: number,
    bounds: google.maps.LatLngBounds,
  ) => void
  enableSearchOnMapLocationChange: boolean
  isPinClickInProgress: React.MutableRefObject<boolean>
  listenersRef: React.MutableRefObject<google.maps.MapsEventListener[]>
  setMap: (map: google.maps.Map | null) => void
}) => {
  const {
    defaultZoom,
    mapReadyCallback,
    onMapChange,
    enableSearchOnMapLocationChange,
    isPinClickInProgress,
    listenersRef,
    setMap,
  } = options

  return (map: google.maps.Map) => {
    setMap(map)
    map.setZoom(defaultZoom || ZOOM.default)

    if (typeof mapReadyCallback === 'function') {
      mapReadyCallback(true)
    }

    if (typeof onMapChange === 'function' && enableSearchOnMapLocationChange) {
      // Clean up previous listeners
      listenersRef.current.forEach((listener) => {
        google.maps.event.removeListener(listener)
      })
      listenersRef.current = []

      const handleMapEvent = () => {
        if (isPinClickInProgress.current) {
          return
        }

        const center = map.getCenter()
        const zoom = map.getZoom()
        const bounds = map.getBounds()

        if (center && zoom && bounds && typeof onMapChange === 'function') {
          onMapChange({ lat: center.lat(), lng: center.lng() }, zoom, bounds)
        }
      }

      listenersRef.current.push(
        map.addListener('dragend', handleMapEvent),
        map.addListener('zoom_changed', handleMapEvent),
      )
    }
  }
}

// Creates a marker click handler function for Google Maps
export const createMarkerClickHandler = (options: {
  map: google.maps.Map | null
  markers: { length: number }
  selectedPinZoom: number | undefined
  defaultZoom: number | undefined
  pinClickCallback?: (markerId: number) => void
  fitBounds: () => void
  isPinClickInProgress: React.MutableRefObject<boolean>
}) => {
  const {
    map,
    markers,
    selectedPinZoom,
    defaultZoom,
    pinClickCallback,
    fitBounds,
    isPinClickInProgress,
  } = options

  return (
    markerPosition: MapLocationProps,
    isInfoWindowOpen: boolean,
    markerId: number | null,
  ) => {
    if (!map) return

    isPinClickInProgress.current = true

    // Use requestAnimationFrame to batch UI updates
    requestAnimationFrame(() => {
      if (isInfoWindowOpen) {
        map.setZoom(selectedPinZoom || ZOOM.selected)
        map.panTo(markerPosition)
        if (typeof pinClickCallback === 'function') {
          pinClickCallback(markerId || 0)
        }
      } else {
        map.setZoom(defaultZoom || ZOOM.default)
        if (markers.length > 1) {
          fitBounds()
        }
      }

      const idleListener = map.addListener('idle', () => {
        // Clear the flag and remove the listener
        isPinClickInProgress.current = false
        google.maps.event.removeListener(idleListener)
      })
    })
  }
}
